import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useApi } from 'app/shared/contexts/api';
import { SubmitFeedbackDto, SubmitFeedbackResponse } from 'app/data/types/feedback';
import { getUnratedFeedbackCandidatesQueryKey } from 'app/data/queries/use-unrated-feedback-candidates-query';

export interface SubmitFeedbackVariables extends SubmitFeedbackDto {
  candidateUuid: string;
}

/**
 * Submit a rating (and optionally a comment) against a feedback candidate.
 *
 * One atomic call: the service rejects a second submit for the same candidate with a 400, so there is
 * no separate "rate now, comment later" step — where comments are collected, the rating waits for them.
 *
 * `retry: false` deliberately. A 400 here means "already submitted", which retrying cannot fix, and a
 * rating is not worth re-sending against a candidate whose state we no longer know.
 */
export const useSubmitFeedbackMutation = (): UseMutationResult<
  SubmitFeedbackResponse,
  Error,
  SubmitFeedbackVariables
> => {
  const { apiUrl, getAuthHeaders } = useApi();
  const queryClient = useQueryClient();

  return useMutation<SubmitFeedbackResponse, Error, SubmitFeedbackVariables>({
    mutationFn: async ({ candidateUuid, rating, comment }: SubmitFeedbackVariables) => {
      const response = await apiRequest<SubmitFeedbackResponse, SubmitFeedbackDto, void>({
        url: `${apiUrl}/api/feedback-candidates/${candidateUuid}/submit`,
        method: HttpMethod.POST,
        headers: await getAuthHeaders(),
        body: comment ? { rating, comment } : { rating },
      });

      if (response.isEmpty()) {
        throw new Error('Failed to submit feedback');
      }

      return response.data;
    },
    // The candidate this rating consumed has left the unrated list, so drop the cached page rather
    // than letting it keep offering stars on an item that is now rated.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getUnratedFeedbackCandidatesQueryKey() });
    },
    retry: false,
  });
};
